import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { ADD_BUTTON_SIZE, HEADER_HEIGHT } from '../../_constants/styleConst';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../stores/store';
import OrganizationTree from './components/OrganizationTree';
import OrganizationUpdate from './components/OrganizationUpdate';
import OrganizationRegisterModal from './components/OrganizationRegisterModal';
import { useRichTreeViewApiRef } from '@mui/x-tree-view/hooks';
import { PutAccountsInfo, PostAccountsInfo } from '../../services/apiTypes';
import {
  fetchAccountsInfo,
  createAccountsInfo,
  updateAccountsInfo,
  deleteAccountsInfo,
} from '../../stores/accountsInfo/slice';
import AddButton from '../../_common/Button/AddButton';
import { isAdmin } from '../../_utils/utils';
import { isFulfilled, isRejected } from '@reduxjs/toolkit';
import { isGetAccountsInfo } from '../../_utils/typeGuardUtils';
import useErrorModal from '../../hooks/useErrorModal';
import ErrorModal from '../../_common/Modal/ErrorModal';
import { isEmptyData } from '../../_utils/utils';
import { API_CODE } from '../../_constants/apiConst';

const ManageOrganization: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const { userData } = useSelector((state: RootState) => state.userInfo);
  /** 組織ツリーの管理*/
  const treeRef = useRichTreeViewApiRef();
  const itemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  /**
   * 編集ダイアログが開いているか
   * true  : 編集コンポーネントを表示
   * false : 新規作成コンポーネントを表示
   * */

  /**　変更中組織のキー */
  const [targetOrganizationKey, setTargetOrganizationKey] =
    useState<string>('');

  const [errorMsg, setErrorMsg] = useState('');
  const {
    error,
    showApiError,
    showTypeError,
    getApiErrorMessage,
    getTypeErrorMessage,
    hideError,
  } = useErrorModal();

  /** 登録モーダル */
  const [isOpenRegisterModal, setIsOpenRegisterModal] = useState(false);

  useEffect(() => {
    (async () => {
      const response = await dispatch(fetchAccountsInfo());
      if (isFulfilled(response)) {
        if (!isGetAccountsInfo(response.payload)) {
          showTypeError(API_CODE.ACCOUNT_INFO);
        }
      } else {
        showApiError(response.payload, API_CODE.ACCOUNT_INFO);
      }
    })();
  }, [dispatch]);

  const showRegisterModal = () => {
    setIsOpenRegisterModal(true);
    setErrorMsg('');
  };
  const showUpdateModal = (organizationKey: string) => {
    setTargetOrganizationKey(organizationKey);
    setErrorMsg('');
  };
  const handleCloseRegisterModal = () => {
    setIsOpenRegisterModal(false);
  };

  const focusOrganization = (key: string) => {
    setTargetOrganizationKey(key);
    if (key !== targetOrganizationKey) {
      setTargetOrganizationKey(key);
      setFocusItem(key);
    }
    const el = itemRefs.current[key];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const setFocusItem = (key: string) => {
    const fakeEvent = new Event('click') as unknown as React.SyntheticEvent;
    treeRef?.current?.setItemSelection({ event: fakeEvent, itemId: key });
  };

  const cleanDataPost = (data: PostAccountsInfo): PostAccountsInfo => {
    return {
      ...data,
      partnerCompanyKey: data.partnerCompanyKey?.filter(
        (item) => !isEmptyData(item),
      ),
    };
  };

  const cleanDataPut = (data: PutAccountsInfo): PutAccountsInfo => {
    return {
      ...data,
      partnerCompanyKey: data.partnerCompanyKey?.filter(
        (item) => !isEmptyData(item),
      ),
    };
  };

  const handleOrganizationAction = useCallback(
    async (action: 'update' | 'delete', organization?: PutAccountsInfo) => {
      if (!organization) {
        return;
      }
      try {
        let result;
        switch (action) {
          case 'update':
            const cleanOrganizationUpdate = cleanDataPut(organization);
            result = await dispatch(
              updateAccountsInfo(cleanOrganizationUpdate),
            );
            break;
          case 'delete':
            const cleanOrganizationDelete = cleanDataPut(organization);
            result = await dispatch(
              deleteAccountsInfo(cleanOrganizationDelete.key),
            );
            break;
          default:
            return;
        }

        if (isRejected(result)) {
          setErrorMsg(
            getApiErrorMessage(result.payload, API_CODE.ACCOUNT_INFO),
          );
          return;
        }
        setErrorMsg('');

        // 各action実行後、全件取得
        const response = await dispatch(fetchAccountsInfo());

        if (isFulfilled(response)) {
          if (!isGetAccountsInfo(response.payload)) {
            showTypeError(API_CODE.ACCOUNT_INFO);
          }
        } else {
          showApiError(response.payload, API_CODE.ACCOUNT_INFO);
          return;
        }
      } catch (error) {
        console.error(`organization ${action} failed:`, error);
      }
    },

    [dispatch, fetchAccountsInfo],
  );

  /** 組織の追加処理 */
  const postAccountsInfo = async (data: PostAccountsInfo) => {
    // エラーがあればメッセージを返す
    try {
      const cleanOrganizationCreate = cleanDataPost(data);
      const createResponse = await dispatch(
        createAccountsInfo(cleanOrganizationCreate),
      );
      if (isRejected(createResponse)) {
        return getApiErrorMessage(
          createResponse.payload,
          API_CODE.ACCOUNT_INFO,
        );
      }
      const response = await dispatch(fetchAccountsInfo());
      if (isFulfilled(response)) {
        if (!isGetAccountsInfo(response.payload)) {
          return getTypeErrorMessage(API_CODE.ACCOUNT_INFO);
        }
      } else {
        return getApiErrorMessage(
          createResponse.payload,
          API_CODE.ACCOUNT_INFO,
        );
      }
    } catch (error) {
      return getApiErrorMessage(error, API_CODE.ACCOUNT_INFO);
    }
    focusOrganization(data.key);
    return '';
  };

  return (
    <Box
      display="flex"
      sx={{
        mt: `calc(${HEADER_HEIGHT} + 1rem)`,
        mb: `calc(${ADD_BUTTON_SIZE} * 1.5)`,
      }}
    >
      <Box
        sx={{
          width: '40%',
          mx: 3,
          mt: '51px',
        }}
      >
        <OrganizationTree
          treeRef={treeRef}
          itemRefs={itemRefs}
          showUpdateModal={showUpdateModal}
        />
      </Box>
      <Box
        sx={{
          width: '50%',
          mx: 3,
        }}
      >
        <OrganizationUpdate
          errorMsg={errorMsg}
          updateErrorMsg={setErrorMsg}
          targetOrganizationKey={targetOrganizationKey}
          focusOrganization={focusOrganization}
          handleOrganizationAction={handleOrganizationAction}
        />
        <OrganizationRegisterModal
          isOpen={isOpenRegisterModal}
          handleClose={handleCloseRegisterModal}
          primaryAgency={targetOrganizationKey}
          postAccountsInfo={postAccountsInfo}
        />
      </Box>
      {isAdmin(userData) && <AddButton onClick={showRegisterModal} />}
      {error && <ErrorModal message={error} onClose={hideError} />}
    </Box>
  );
};

export default ManageOrganization;
